import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { SERVICES } from "@/pages/public/PublicSite";
import { Check, ArrowRight, Star, MessageCircle, Calendar as CalendarIcon, HelpCircle } from "lucide-react";

/* Per-service editorial payload. Falls back to sensible defaults when a
 * particular service hasn't been hand-authored yet — every page still ships
 * with a full hero / benefits / process / FAQ / portfolio / testimonials /
 * related-services stack. */
const DETAIL = {
  "website-design": {
    tagline: "High-converting websites that turn visitors into customers.",
    benefits: [
      "Custom-designed to your brand — no templates",
      "Mobile-first, blazing fast (Lighthouse 95+)",
      "Built-in SEO foundations from day one",
      "Analytics + conversion tracking pre-installed",
      "Owned by you — full source code + hosting freedom",
    ],
    process: [
      { t: "Discovery", d: "Deep-dive workshop to map your goals, audience and success metrics." },
      { t: "Design", d: "Wireframes → high-fidelity mockups. 3 revision rounds included." },
      { t: "Build", d: "Development in staging with weekly demos so nothing is a surprise." },
      { t: "Launch", d: "Content upload, QA across devices, DNS cutover in a single window." },
      { t: "Grow", d: "Post-launch analytics review at 30/60/90 days." },
    ],
    faq: [
      { q: "How long does a website take?", a: "Most custom websites launch in 4–8 weeks depending on scope and content readiness." },
      { q: "Do I own the code?", a: "Yes. You own 100% of the codebase and can host anywhere." },
      { q: "Can you write the content?", a: "Absolutely — copywriting is available as an add-on and included in Professional/Enterprise." },
    ],
  },
  "seo": {
    tagline: "Rank higher, capture demand, drive qualified traffic that actually converts.",
    benefits: [
      "Keyword strategy tied to buyer intent",
      "Technical SEO fixes + Core Web Vitals",
      "On-page optimization for every URL",
      "Local SEO for map-pack visibility",
      "Monthly reporting dashboard",
    ],
    process: [
      { t: "Audit", d: "Full technical + content audit against 200-point checklist." },
      { t: "Strategy", d: "Priority-ranked action plan with expected traffic lift." },
      { t: "Execute", d: "Technical fixes, on-page tune-ups, content creation." },
      { t: "Measure", d: "Weekly rank tracking, monthly performance report." },
    ],
    faq: [
      { q: "When will I see results?", a: "Technical wins land in weeks; content-driven ranking typically compounds over 3–6 months." },
      { q: "Do you guarantee rankings?", a: "No credible SEO agency does. We guarantee process, transparency and effort." },
    ],
  },
  "google-ads": {
    tagline: "Paid Google campaigns engineered to generate qualified calls at a cost-per-lead that actually scales.",
    benefits: [
      "Conversion-focused search campaigns targeting buyers ready to call",
      "Dedicated landing pages engineered for phone calls and form leads",
      "Call tracking on every ad click — know which campaign and keyword drove the call",
      "Weekly negative-keyword pruning to protect your budget",
      "Transparent monthly reporting — real cost per lead, no vanity metrics",
    ],
    process: [
      { t: "Audit", d: "Review your current ads (if any), Google Business, and website conversion path." },
      { t: "Build", d: "Launch tightly-scoped campaigns tied to call-tracked landing pages." },
      { t: "Track", d: "Every call is recorded and attributed to the exact keyword and ad." },
      { t: "Optimize", d: "Weekly bid adjustments, negative keywords, and A/B tests on ad copy." },
    ],
    faq: [
      { q: "How much ad spend do I need?", a: "We recommend a minimum of $1,500/mo in ad spend for meaningful data. Management fee is separate." },
      { q: "Do you require a long contract?", a: "No. Month-to-month. If it's not working, we say so — and either fix it or part ways." },
      { q: "Do you guarantee leads?", a: "No credible agency does. We guarantee transparency, structured optimization, and honest reporting." },
    ],
  },
  "call-tracking": {
    tagline: "Measure every inbound call, know exactly which channel drove it, and never miss a lead again.",
    benefits: [
      "A unique tracking number for every marketing channel",
      "Call recording (with consent) for training and quality control",
      "Missed-call SMS follow-up so leads don't slip",
      "Attribution reports tied to Google Ads, SEO, GBP, and referrals",
      "Integrates directly with the Yosi CRM",
    ],
    process: [
      { t: "Setup", d: "Provision tracking numbers for each channel — Google Ads, organic, GBP, print, referrals." },
      { t: "Route", d: "Configure ring groups, business hours, and missed-call SMS fallback." },
      { t: "Report", d: "Weekly attribution reports show what's producing calls and what isn't." },
      { t: "Optimize", d: "Reallocate budget toward the channels that actually drive booked jobs." },
    ],
    faq: [
      { q: "Will call tracking hurt my Google ranking?", a: "No. Tracking numbers are used for display; the primary NAP-consistent number remains on your Google Business Profile." },
      { q: "Do I need to record calls?", a: "Recording is optional and configurable per number. We follow all state-level consent requirements." },
    ],
  },
  "lead-management": {
    tagline: "Structured follow-up sequences that turn cold interest into signed customers.",
    benefits: [
      "Every lead captured into the Yosi CRM automatically — from forms, calls, GBP, and ads",
      "Automated first-response SMS + email within 2 minutes of a new lead",
      "Structured multi-step follow-up sequences per lead source",
      "Assignment rules so leads route to the right person instantly",
      "Weekly pipeline reports — see exactly what's stalled and where",
    ],
    process: [
      { t: "Capture", d: "Every source (forms, ads, GBP, calls) piped into a single CRM." },
      { t: "Respond", d: "Auto SMS + email within 2 minutes — the #1 predictor of conversion." },
      { t: "Sequence", d: "3–7 touch follow-up per lead type, mixing SMS, email, and call reminders." },
      { t: "Convert", d: "Sales pipeline stages tracked with clear next-action ownership." },
      { t: "Report", d: "Weekly reports show conversion rate, response time, and dropped leads." },
    ],
    faq: [
      { q: "Do I need a separate CRM?", a: "No. The Yosi CRM is included. If you already use HubSpot or Salesforce, we can integrate." },
      { q: "Will you make the follow-up calls for me?", a: "We build and manage the automation. Human follow-up calls can be added as a managed service." },
    ],
  },
  "ai": {
    tagline: "Deploy AI agents that qualify leads, answer questions, and automate ops 24/7.",
    benefits: [
      "24/7 lead qualification and booking",
      "Multi-language customer support",
      "Integrates with your existing CRM",
      "Trained on your knowledge base + tone",
      "Human hand-off for complex cases",
    ],
    process: [
      { t: "Discovery", d: "Map the workflows you want to automate + guardrails." },
      { t: "Training", d: "Ingest your knowledge base, docs, past tickets." },
      { t: "Pilot", d: "2-week supervised pilot with real conversations." },
      { t: "Launch", d: "Deploy to your website, WhatsApp, or messenger." },
    ],
    faq: [
      { q: "Which AI model do you use?", a: "Depends on your use case — GPT class for depth, Claude/Gemini for specific strengths. We benchmark for you." },
      { q: "Is my data safe?", a: "Yes. Data is scoped per client and never used to train shared models." },
    ],
  },
};

const TESTIMONIALS = [
  { q: "Yosi rebuilt our online presence in 6 weeks and doubled our leads in the first quarter.", author: "Sarah M.", role: "Owner, Coastal Bistro" },
  { q: "Their team feels like an in-house squad — we replaced 4 vendors with one Yosi engagement.", author: "David R.", role: "COO, Future Construction" },
  { q: "The Google Business Profile work paid for itself in the first month.", author: "Dr. Patel", role: "Downtown Medical Clinic" },
];

export default function ServiceDetail() {
  const { key } = useParams();
  const service = SERVICES.find((s) => s.key === key);
  if (!service) return <Navigate to="/services" replace />;

  const meta = DETAIL[key] || {
    tagline: service.desc,
    benefits: [
      "Delivered by senior specialists — no juniors",
      "Fixed scope and timeline you can plan around",
      "Integrated with your CRM + client portal from day one",
      "Bilingual delivery (English & Spanish)",
      "30-day post-launch support included",
    ],
    process: [
      { t: "Discovery", d: "Workshop to align on goals, scope and success metrics." },
      { t: "Plan", d: "Detailed proposal with timeline and fixed pricing." },
      { t: "Deliver", d: "Weekly progress demos in your client portal." },
      { t: "Launch", d: "Handoff with training, docs and post-launch support." },
    ],
    faq: [
      { q: "How does pricing work?", a: "Every engagement is scoped and quoted individually — no hourly billing surprises." },
      { q: "What's your typical timeline?", a: "Small engagements ship in 2–4 weeks; larger builds 6–12 weeks. We commit to dates and hit them." },
    ],
  };
  const related = SERVICES.filter((s) => s.key !== key).slice(0, 4);
  const Icon = service.icon;

  const qs = `?service=${encodeURIComponent(service.name)}`;

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800/60" data-testid={`svc-detail-${key}`}>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/services" className="text-xs text-blue-400 hover:text-blue-300 uppercase tracking-widest mb-4 inline-block">← All services</Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium mb-4">
              <Icon className="w-3.5 h-3.5" /> Service
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-4">{service.name}</h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">{meta.tagline}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/contact${qs}`} data-testid={`svc-detail-quote-${key}`} className="btn-scale h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center gap-2 shadow-xl shadow-blue-500/40">
                Request Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={`/contact${qs}&intent=consultation`} data-testid={`svc-detail-schedule-${key}`} className="btn-scale h-12 px-6 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-200 font-semibold text-sm inline-flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" /> Schedule Consultation
              </Link>
            </div>
          </div>
          <div className="relative rounded-2xl aspect-[4/3] bg-gradient-to-br from-blue-500/20 to-cyan-500/5 border border-slate-800 grid place-items-center">
            <Icon className="w-24 h-24 text-blue-400/70" />
            <span className="absolute text-[10px] uppercase tracking-widest text-slate-500 top-4 right-4">Image placeholder</span>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="svc-benefits">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Benefits</div>
        <h2 className="font-display text-3xl font-bold mb-8">Why {service.name.toLowerCase()} with Yosi?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {meta.benefits.map((b) => (
            <div key={b} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <Check className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span className="text-slate-200 text-sm leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-900/40 border-y border-slate-800/60 py-16" data-testid="svc-process">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Our process</div>
          <h2 className="font-display text-3xl font-bold mb-10">A predictable path from kickoff to launch.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {meta.process.map((p, i) => (
              <div key={p.t} className="rounded-xl border border-slate-800 bg-slate-950/60 p-5 relative">
                <div className="absolute -top-3 left-5 h-6 px-2.5 rounded-full bg-blue-500 text-[11px] font-bold text-white inline-flex items-center">STEP {i + 1}</div>
                <h3 className="font-display font-semibold text-lg mt-3 mb-2">{p.t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio placeholders */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="svc-portfolio">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Recent work</div>
            <h2 className="font-display text-3xl font-bold">{service.name} case studies</h2>
          </div>
          <Link to="/portfolio" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">See all case studies <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden group hover:border-blue-500/40 transition-all">
              <div className="aspect-[4/3] bg-gradient-to-br from-blue-500/15 to-cyan-500/5 grid place-items-center relative">
                <Icon className="w-16 h-16 text-blue-400/60" />
                <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-900/70 px-2 py-0.5 rounded">Case study {i}</span>
              </div>
              <div className="p-4">
                <div className="text-slate-100 font-medium">Featured Client {i}</div>
                <div className="text-xs text-slate-500 mt-1">{service.name}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-slate-900/40 border-y border-slate-800/60 py-16" data-testid="svc-testimonials">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Testimonials</div>
          <h2 className="font-display text-3xl font-bold mb-10">What clients say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.author} className="rounded-xl border border-slate-800 bg-slate-950/60 p-6">
                <div className="flex gap-1 mb-4">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4 text-blue-400 fill-blue-400" />)}</div>
                <p className="text-sm text-slate-200 leading-relaxed mb-4 italic">"{t.q}"</p>
                <div className="text-sm text-slate-100 font-semibold">{t.author}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-16" data-testid="svc-faq">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">FAQ</div>
        <h2 className="font-display text-3xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-3">
          {meta.faq.map((f, i) => (
            <details key={f.q} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer flex items-center gap-2 text-slate-100 font-medium">
                <HelpCircle className="w-4 h-4 text-blue-400 shrink-0" />
                <span>{f.q}</span>
              </summary>
              <p className="text-sm text-slate-400 leading-relaxed mt-3 pl-6">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16" data-testid="svc-related">
        <h2 className="font-display text-2xl font-bold mb-6">Related services</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {related.map((s) => {
            const RIcon = s.icon;
            return (
              <Link key={s.key} to={`/services/${s.key}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-blue-500/40 transition-all">
                <RIcon className="w-6 h-6 text-blue-400 mb-3" />
                <div className="text-sm font-medium text-slate-100">{s.name}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto text-center px-6 pb-20" data-testid="svc-cta">
        <h2 className="font-display text-3xl font-bold mb-4">Ready to start your {service.name.toLowerCase()} project?</h2>
        <p className="text-slate-400 mb-8">Book a discovery call — free, no pressure, 30 minutes.</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to={`/contact${qs}`} className="btn-scale h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center gap-2 shadow-xl shadow-blue-500/40">
            Request Quote <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to={`/contact${qs}&intent=consultation`} className="btn-scale h-12 px-6 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-200 font-semibold text-sm inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Talk to Us
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
